import { useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from '../contexts/AuthContext'
import apiClient from '../services/apiClient'
import { useSidebar } from '../contexts/SidebarContext'

export default function TemplatePickerPage() {
  const { auth } = useContext(AuthContext)
  const { openLibrary } = useSidebar()

  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [preview, setPreview] = useState(null)

  // Gallery controls
  const [filter, setFilter] = useState('recommended') // recommended | available | premium | new
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState('recommended') // recommended | newest | az | za

  useEffect(() => {
    apiClient
      .get('/api/templates/')
      .then((res) => setTemplates(res.data || []))
      .catch((err) => console.error('❌ Failed to load templates:', err))
      .finally(() => setLoading(false))
  }, [])

  const buildImageUrl = (path) => {
    if (!path) return ''
    if (/^https?:\/\//i.test(path)) return path
    return `${process.env.REACT_APP_API_BASE_URL}${path}`
  }

  const userTemplateCodes = auth?.user?.templates || []

  const canUseTemplate = (template) => {
    if (typeof template.accessible === 'boolean') return template.accessible
    return userTemplateCodes.includes(template.code)
  }

  // Frontend-only “New” support (auto-works later if you add tags/isNew from API)
  const isNewTemplate = (template) => {
    if (template?.isNew) return true
    if (Array.isArray(template?.tags)) {
      return template.tags.some((t) => String(t).toLowerCase() === 'new')
    }
    return false
  }

  const handleSelect = (template) => {
    if (!canUseTemplate(template)) return
    localStorage.setItem('pending_template_code', template.code)
    openLibrary()
    setPreview(null)
  }

  const counts = useMemo(() => {
    const total = templates.length
    const available = templates.filter(canUseTemplate).length
    const premium = templates.filter((t) => !!t.isPremium).length
    const fresh = templates.filter(isNewTemplate).length
    return { total, available, premium, fresh }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, auth])

  const filteredTemplates = useMemo(() => {
    const q = query.trim().toLowerCase()
    let list = templates

    // Filter
    if (filter === 'available') list = list.filter(canUseTemplate)
    if (filter === 'premium') list = list.filter((t) => !!t.isPremium)
    if (filter === 'new') list = list.filter(isNewTemplate)

    // Search
    if (q) list = list.filter((t) => (t?.title || '').toLowerCase().includes(q))

    const byTitleAsc = (a, b) => (a?.title || '').localeCompare(b?.title || '')
    const byTitleDesc = (a, b) => (b?.title || '').localeCompare(a?.title || '')

    // “Recommended” bucket: accessible first, then premium, then locked
    const bucket = (t) => {
      const acc = canUseTemplate(t)
      const prem = !!t.isPremium
      if (acc && !prem) return 0
      if (acc && prem) return 1
      if (!acc && !prem) return 2
      return 3
    }

    const byRecommended = (a, b) => {
      const d = bucket(a) - bucket(b)
      if (d !== 0) return d
      return byTitleAsc(a, b)
    }

    // “Newest” (frontend-only): tries createdAt/created_at; falls back safely
    const getTs = (t) => {
      const raw = t?.createdAt || t?.created_at
      const ts = raw ? Date.parse(raw) : NaN
      if (!Number.isNaN(ts)) return ts
      if (typeof t?.id === 'number') return t.id
      return 0
    }

    const sorted = [...list]
    if (sort === 'az') sorted.sort(byTitleAsc)
    else if (sort === 'za') sorted.sort(byTitleDesc)
    else if (sort === 'newest') sorted.sort((a, b) => getTs(b) - getTs(a))
    else sorted.sort(byRecommended)

    return sorted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, filter, query, sort, auth])

  return (
    <div className="px-3 sm:px-4 py-6 sm:py-8">
      <div className="max-w-6xl mx-auto">
        {/* Gallery header */}
        <div className="rounded-2xl border border-[#eadfd8] bg-[#fbf7f4] overflow-hidden shadow-sm">
          <div className="px-4 sm:px-6 py-6 sm:py-8 bg-white">
            <h1 className="text-center text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-[#5a3e36]">
              Curate Your Recipe
            </h1>
            <p className="mt-2 text-center text-sm sm:text-base text-[#7a6f66] max-w-2xl mx-auto">
              The complete Ruoth app is free, with one design included. Buy only the additional card styles you want.
            </p>
          </div>

          {/* Controls */}
          <div className="px-4 sm:px-6 py-4 border-t border-[#eadfd8]">
            {/* Filter chips */}
            <div className="flex gap-2 justify-start overflow-x-auto pb-2 px-2 sm:px-0 scroll-px-2 templates-scroll">
              {[
                { key: 'recommended', label: 'Recommended' },
                { key: 'available', label: `Available${counts.available ? ` (${counts.available})` : ''}` },
                { key: 'premium', label: `Premium${counts.premium ? ` (${counts.premium})` : ''}` },
                { key: 'new', label: `New${counts.fresh ? ` (${counts.fresh})` : ''}` },
              ].map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setFilter(chip.key)}
                  className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold border transition whitespace-nowrap shadow-sm active:scale-[0.98] ${
                    filter === chip.key
                      ? 'bg-[#5a3e36] text-white border-[#5a3e36]'
                      : 'bg-white text-[#5a3e36] border-[#eadfd8] hover:bg-[#f5eee9]'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>

            {/* Search + Sort */}
            <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="flex-1">
                <label className="sr-only" htmlFor="template-search">
                  Search templates
                </label>
                <div className="relative">
                  <input
                    id="template-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search templates…"
                    className="w-full sm:max-w-md rounded-xl border border-[#eadfd8] bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#c7a795]"
                  />
                  {query ? (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded-lg text-[#7a6f66] hover:bg-[#f5eee9]"
                      aria-label="Clear search"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="flex items-center gap-2 justify-between sm:justify-end">
                <div className="text-xs sm:text-sm text-[#7a6f66]">
                  Templates: <span className="font-semibold text-[#5a3e36]">{filteredTemplates.length}</span>
                </div>

                <label className="sr-only" htmlFor="template-sort">
                  Sort templates
                </label>
                <select
                  id="template-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="rounded-xl border border-[#eadfd8] bg-white px-3 py-2 text-sm font-semibold text-[#5a3e36] outline-none focus:ring-2 focus:ring-[#c7a795]"
                >
                  <option value="recommended">Sort: Recommended</option>
                  <option value="newest">Sort: Newest</option>
                  <option value="az">Sort: A–Z</option>
                  <option value="za">Sort: Z–A</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mt-6">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-[#eadfd8] bg-white overflow-hidden shadow-sm animate-pulse">
                  <div className="w-full aspect-[2/3] bg-[#f1e9e4]" />
                  <div className="p-3">
                    <div className="h-4 w-3/4 bg-[#f1e9e4] rounded" />
                    <div className="mt-2 h-3 w-1/2 bg-[#f1e9e4] rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-lg font-semibold text-[#5a3e36]">No templates found</div>
              <div className="mt-1 text-sm text-[#7a6f66]">Try a different filter or search.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {filteredTemplates.map((template) => {
                const hasAccess = canUseTemplate(template)
                const imageUrl = buildImageUrl(template.previewImage)

                return (
                  <button
                    key={template.code}
                    type="button"
                    onClick={() => setPreview(template)} // preview even if locked
                    className="group relative text-left rounded-2xl border border-[#eadfd8] overflow-hidden transition-all duration-200 shadow-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c7a795] hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99]"
                    title="Tap to preview"
                  >
                    {/* Badges */}
                    <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
                      {hasAccess ? (
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-700/90 text-white shadow">
                          Available
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-black/60 text-white shadow">
                          Locked
                        </span>
                      )}
                    </div>

                    {template.isPremium ? (
                      <div className="absolute top-3 right-3 z-10">
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-500/90 text-white shadow">
                          Premium
                        </span>
                      </div>
                    ) : null}

                    {/* Image */}
                    <div className="relative">
                      <img
                        src={imageUrl}
                        alt={template.title}
                        className="w-full aspect-[2/3] object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                      <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-white/90 text-[#5a3e36] shadow">
                          Preview
                        </span>
                      </div>
                    </div>

                    {/* Label */}
                    <div className="p-3 sm:p-4">
                      <div className="font-semibold text-sm sm:text-[15px] text-[#5a3e36] truncate">
                        {template.title}
                      </div>
                      <div className="mt-0.5 text-xs text-[#7a6f66]">
                        {hasAccess ? 'Available in your account' : 'Unlock this premium design'}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Modal preview */}
        {preview && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-50 flex items-center justify-center p-4"
            onClick={() => setPreview(null)}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-[#eadfd8] shadow-sm hover:bg-[#f5eee9]"
                aria-label="Close preview"
                title="Close"
              >
                ✕
              </button>

              <img
                src={buildImageUrl(preview.previewImage)}
                alt={preview.title}
                className="w-full aspect-[2/3] object-cover"
              />

              <div className="p-5 text-center">
                <h2 className="text-xl font-semibold text-[#5a3e36]">{preview.title}</h2>

                <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
                  {preview.isPremium ? (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-700 border border-amber-200">
                      Premium
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-600/10 text-emerald-700 border border-emerald-200">
                      Standard
                    </span>
                  )}

                  {canUseTemplate(preview) ? (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-600/10 text-emerald-700 border border-emerald-200">
                      Available in your account
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                      Unlock this premium design
                    </span>
                  )}
                </div>

                {canUseTemplate(preview) ? (
                  <>
                    <button
                      type="button"
                      onClick={() => handleSelect(preview)}
                      className="mt-4 w-full bg-[#5a3e36] text-white py-2.5 rounded-xl font-semibold text-sm hover:bg-[#432e29] active:scale-[0.99] transition"
                    >
                      Use This Design
                    </button>
                    <p className="mt-2 text-xs text-[#7a6f66]">
                      Next: tap <span className="font-semibold">New</span> in your Library to start your recipe.
                    </p>
                  </>
                ) : (
                  <div className="mt-4 w-full bg-[#f5eee9] text-[#5a3e36] py-2.5 rounded-xl font-semibold text-sm border border-[#eadfd8]">
                    Unlock this premium design
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
